import { Action, WelcomeSectionListType } from '@wix/add-panel-common';
import { Button, Composites, PromotionalList, TextLabel } from '@wix/wix-base-ui';
import React, { FC } from 'react';
import { BIOrigin } from '../../../../../constants/BI';
import { HelpActionInitiator, useActionsDispatcher, useGenericActionsDispatcher } from '../../../../ActionsDispatcher';
import { useCurrentCategory } from '../../../../CurrentCategoryProvider';
import { useUtils } from '../../../../UtilsProvider';
import { useFullBannerImgSrc } from './hooks/useFullBannerImgSrc';
import styles from './WelcomeSection.scss';
import type { WelcomeSectionProps } from './WelcomeSection.types';

export const WelcomeSection: FC<WelcomeSectionProps> = ({ section }) => {
  const category = useCurrentCategory();
  const { id: categoryId } = category;

  const { automationId, props } = section;
  const {
    mainAction,
    secondaryAction,
    bannerImgSrc,
    bannerRetinaImgSrc,
    listType,
    listItems,
    title,
    subtitle,
    mainActionLabel,
    secondaryActionLabel,
    helpId,
  } = props;

  const { translate } = useUtils();

  const { dispatchHelpClick } = useActionsDispatcher();
  const dispatchGenericAction = useGenericActionsDispatcher();

  const createActionClickHandler = (action?: Action) => () => {
    if (!action) {
      return;
    }

    if (action === Action.HelpClick) {
      dispatchHelpClick({
        helpId,
        categoryId,
        initiator: HelpActionInitiator.WelcomeSection,
        biHelpParams: {
          origin: BIOrigin.AddPanel,
          component: categoryId,
          panel_name: BIOrigin.AddPanel,
          learn_more: false,
        },
      });
      return;
    }

    dispatchGenericAction(action, { section, category });
  };

  const fullBannerImgSrc = useFullBannerImgSrc(bannerImgSrc);
  const fullBannerRetinaImgSrc = useFullBannerImgSrc(bannerRetinaImgSrc);

  return (
    <div data-hook={automationId} className={styles.welcomeSection}>
      <div className={styles.bannerWrapper}>
        <picture>
          {fullBannerRetinaImgSrc && <source srcSet={`${fullBannerRetinaImgSrc} 2x`} />}
          {fullBannerImgSrc && <source srcSet={`${fullBannerImgSrc} 1x`} />}
          <img src={fullBannerImgSrc} className={styles.banner} alt="" />
        </picture>
      </div>

      <div className={styles.titleContainer}>
        <TextLabel
          shouldTranslate={false}
          dataHook="welcome_title"
          enableEllipsis={false}
          type="T16"
          value={translate(title)}
          className={styles.title}
        />
        <TextLabel
          shouldTranslate={false}
          type="T11"
          dataHook="welcome_subtitle"
          enableEllipsis={false}
          value={translate(subtitle)}
        />
      </div>

      {listType === WelcomeSectionListType.Promotional && (
        <Composites.PromotionalList className={styles.promotionalList}>
          <PromotionalList items={listItems} dataHook="promotional-list" />
        </Composites.PromotionalList>
      )}

      {listType === WelcomeSectionListType.Numeric && (
        <div className={styles.numericListWrapper} data-hook="numeric-list">
          <ol>
            {listItems.map((item, itemIndex) => (
              <li key={item} className={styles.numericListItem}>
                <TextLabel value={`${itemIndex + 1}.`} type="T05" enableEllipsis={false} shouldTranslate={false} />
                <TextLabel
                  className={styles.numericListItemValue}
                  shouldTranslate={false}
                  value={translate(item)}
                  type="T05"
                  enableEllipsis={false}
                />
              </li>
            ))}
          </ol>
        </div>
      )}

      <Composites.ActionSetVertical>
        {mainActionLabel && (
          <Button dataHook="welcome-section-main-action" onClick={createActionClickHandler(mainAction)}>
            <span>{translate(mainActionLabel)}</span>
          </Button>
        )}
        {secondaryActionLabel && (
          <Button
            dataHook="welcome-section-secondary-action"
            onClick={createActionClickHandler(secondaryAction)}
            className="btn-text"
          >
            <span>{translate(secondaryActionLabel)}</span>
          </Button>
        )}
      </Composites.ActionSetVertical>
    </div>
  );
};
